import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import clsx from 'clsx';
import {
  useApprovePasswordStore,
  useCheckLimitStore,
  useGetAccountsStore,
  useGetDictionariesStore,
  useInternalStore,
  useNotificationStore,
} from '@lib/store';
import { AmountUtils } from '@shared/utils';
import { RequestType } from '@shared/api/rest/models';
import { ButtonV2, Constants, ErrorMessage, Input, ItemsType, Modal, Select } from '@shared/widgets';

interface IForm {
  debitAccount?: ItemsType;
  creditAccount?: ItemsType;
  amount?: number;
  currency?: ItemsType;
}
const DEFAULT_VALUES: IForm = { debitAccount: undefined, creditAccount: undefined, amount: undefined, currency: undefined };

interface TransferBetweenMyAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TransferBetweenMyAccountModal = (props: TransferBetweenMyAccountModalProps) => {
  const accountStore = useGetAccountsStore();
  const dictionariesStore = useGetDictionariesStore();
  const checkLimitStore = useCheckLimitStore();
  const internalStore = useInternalStore();
  const approvePasswordStore = useApprovePasswordStore();
  const notificationStore = useNotificationStore();

  const [isContinue, setIsContinue] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [selectedCurrency, setSelectedCurrency] = useState<ItemsType[]>([]);
  const [amountNotification, setAmountNotification] = useState<boolean>(false);

  const { formState, control, handleSubmit, watch, setValue, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const debitAccount = watch('debitAccount');
  const creditAccount = watch('creditAccount');
  const amount = watch('amount');
  const currency = watch('currency');

  const accountOptions = useMemo(
    () => accountStore.data?.map((item) => ({ value: item.Id, label: item.Amount, leftLabel: item.Currency })),
    [accountStore.data],
  );

  useEffect(() => {
    setValue('debitAccount', accountOptions[0]);
    setValue('creditAccount', accountOptions[1]);
  }, [props.isOpen]);

  useEffect(() => {
    const makeSelectCurrency = (dictionariesStore.data?.Currency || [])
      .filter((item) => item.Name === debitAccount?.leftLabel || item.Name === creditAccount?.leftLabel)
      .map((item) => ({ value: item.Id, label: item.Name, leftLabel: item.Name }));

    if (makeSelectCurrency.length > 0) setValue('currency', makeSelectCurrency[0]);
    setSelectedCurrency(makeSelectCurrency);
  }, [debitAccount, creditAccount]);

  const submitHandler = async (data: IForm) => {
    try {
      if (Number(debitAccount?.label) < amount!) {
        setAmountNotification(true);
        return undefined;
      }

      const result = await checkLimitStore.checkLimit({
        accountIdFrom: data.debitAccount?.value,
        accountIdTo: data.creditAccount?.value,
        amount: Number(data.amount),
        currency: data.currency?.value,
        requestType: RequestType.Refill,
      });
      if (result.data?.LimitExceeded) {
        notificationStore.setError(result.data.Note);
        return;
      }

      await internalStore.post({
        SourceAccount: data.debitAccount?.value,
        TargetAccount: data.creditAccount?.value,
        Amount: data.amount,
        Currency: data.currency?.value,
      });
      setIsContinue(true);
    } catch {
      setIsContinue(false);
    }
  };

  useEffect(() => {
    if (amountNotification) notificationStore.setError(Constants.AMOUNT_ERROR);
    setAmountNotification(false);
  }, [amountNotification]);

  const confirmModal = async () => {
    await approvePasswordStore.approvePassword({ RequestId: internalStore.data?.Id, Password: password });
    notificationStore.setSuccess(Constants.DEFAULT_SUCCESS_MESSAGE);
    props.onClose();
    setIsContinue(false);
    reset(DEFAULT_VALUES);
  };

  const onCloseModal = () => {
    props.onClose();
    setIsContinue(false);
    setPassword('');
  };

  const handleBackButtonClick = () => {
    setIsContinue(false);
    setPassword('');
  };

  return (
    <Modal isOpen={props.isOpen} onClose={onCloseModal} title='Internal Transfer'>
      {!isContinue ? (
        <form className='w-full' onSubmit={handleSubmit(submitHandler)}>
          <div className='flex flex-col gap-3'>
            <Controller
              name='debitAccount'
              control={control}
              rules={{ required: 'This field is required' }}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Debit Account</div>
                  <Select
                    wrapperClassName='!p-0'
                    double
                    items={accountOptions}
                    value={value?.value}
                    onChange={(e) => {
                      if (e?.value === creditAccount?.value) setValue('creditAccount', debitAccount);
                      onChange(e);
                    }}
                  />
                </div>
              )}
            />
            <Controller
              name='creditAccount'
              control={control}
              rules={{ required: 'This field is required' }}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Credit Account</div>
                  <Select
                    wrapperClassName='!p-0'
                    double
                    items={accountOptions.length > 1 ? accountOptions : []}
                    value={value?.value}
                    onChange={(e) => {
                      if (e?.value === debitAccount?.value) setValue('debitAccount', creditAccount);
                      onChange(e);
                    }}
                  />
                </div>
              )}
            />
            <Controller
              name='amount'
              control={control}
              rules={{ required: 'This field is required', maxLength: 100 }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <div className={clsx('flex w-full flex-col gap-1')}>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Amount</div>
                  <Input
                    className='!h-[46px]'
                    variant='outlined'
                    rounded
                    type='number'
                    error={error?.message}
                    name='Amount'
                    placeholder='Enter Amount'
                    required
                    value={value}
                    onChange={(value) => AmountUtils.amountChange(value, onChange)}
                  />
                  <ErrorMessage text={error?.message} />
                </div>
              )}
            />
            {selectedCurrency && (
              <Controller
                name='currency'
                control={control}
                defaultValue={selectedCurrency?.[0]}
                rules={{ required: 'This field is required' }}
                render={({ field: { value, onChange } }) => (
                  <div className='flex w-full flex-col gap-1'>
                    <div className='text-xs font-semibold text-[#929292] uppercase'>Currency</div>
                    <Select wrapperClassName='!p-0' value={value?.value} onChange={onChange} items={selectedCurrency} placeholder='CURRENCY' />
                  </div>
                )}
              />
            )}
          </div>
          <div className='mt-6.5 flex items-center justify-between gap-2.5'>
            <ButtonV2 className='!h-[46px]' color='outlined' fullWidth onClick={props.onClose}>
              Cancel
            </ButtonV2>
            <ButtonV2 className='!h-[46px]' fullWidth type='submit' disabled={!formState.isValid || formState.isSubmitting}>
              Continue
            </ButtonV2>
          </div>
        </form>
      ) : (
        <div className='w-full'>
          <div className='mb-4'>
            <div>
              <div className='mb-1 text-xs font-semibold text-[#929292] uppercase'>Debit Account:</div>
              <div className='text-sm font-semibold text-[#fcfcfc] uppercase'>
                <div className='flex items-start justify-start'>
                  <div className='mr-5'>{debitAccount?.leftLabel}</div>
                  <div className='mr-5 whitespace-nowrap'>{AmountUtils.transformToAmount(debitAccount?.label as number)}</div>
                </div>
              </div>
            </div>
            <div>
              <div className='mb-1 text-xs font-semibold text-[#929292] uppercase'>Credit Account:</div>
              <div className='text-sm font-semibold text-[#fcfcfc] uppercase'>
                <div className='flex items-start justify-start'>
                  <div className='mr-5'>{creditAccount?.leftLabel}</div>
                  <div className='mr-5 whitespace-nowrap'>{AmountUtils.transformToAmount(creditAccount?.label as number)}</div>
                </div>
              </div>
            </div>
            <div>
              <div className='mb-1 text-xs font-semibold text-[#929292] uppercase'>Amount:</div>
              <div className='text-sm font-semibold text-[#fcfcfc] uppercase'>
                <div className='flex items-start justify-start'>
                  <div className='mr-5'>{currency?.label}</div>
                  <div className='mr-5 whitespace-nowrap'>{AmountUtils.transformToAmount(Number(amount))}</div>
                </div>
              </div>
            </div>
          </div>
          <div className='mt-3'>
            <div className='mb-1 text-sm font-semibold text-[#666] uppercase'>To confirm Top Up please enter your password</div>
            <Input
              variant='outlined'
              rounded
              className='h-[48px]'
              placeholder='Password'
              name='Password'
              value={password}
              type='password'
              onChange={setPassword}
              required
            />
          </div>
          <div className='mt-4 flex w-full flex-row items-center justify-between gap-4'>
            <ButtonV2 color='outlined' fullWidth onClick={handleBackButtonClick}>
              Back
            </ButtonV2>
            <ButtonV2 fullWidth disabled={password ? password.length < 1 : true} color='primary' onClick={confirmModal}>
              Confirm
            </ButtonV2>
          </div>
        </div>
      )}
    </Modal>
  );
};
